import MediaInfoFactory from "mediainfo.js";
import { getWidgetBaseUrl } from "./origin";

export interface VideoMetadata {
  width: number;
  height: number;
  duration: number; // seconds
  fps: number;
  colorSpace?: string;
  hasAudioTrack: boolean;
}

const MAX_SIZE = 500 * 1024 * 1024;
const MAX_DURATION = 300;
const MAX_DIMENSION = 1920;
const ALLOWED_TYPES = ["video/mp4", "video/quicktime"];

export async function extractVideoMetadata(file: File): Promise<VideoMetadata> {
  const baseUrl = getWidgetBaseUrl();
  const mediainfo = await MediaInfoFactory({
    format: "object",
    locateFile: (path: string) => `${baseUrl}/widget/${path}`,
  });

  const getSize = () => file.size;
  const readChunk = async (
    chunkSize: number,
    offset: number
  ): Promise<Uint8Array> =>
    new Uint8Array(await file.slice(offset, offset + chunkSize).arrayBuffer());

  const result: any = await mediainfo.analyzeData(getSize, readChunk);
  const videoTrack = result?.media?.track?.find(
    (t: any) => t["@type"] === "Video"
  );
  const audioTrack = result?.media?.track?.find(
    (t: any) => t["@type"] === "Audio"
  );

  return {
    width: Number(videoTrack?.Width ?? 0),
    height: Number(videoTrack?.Height ?? 0),
    duration: Number(videoTrack?.Duration ?? 0),
    fps: Number(videoTrack?.FrameRate ?? 30),
    colorSpace: videoTrack?.ColorSpace,
    hasAudioTrack: !!audioTrack,
  };
}

export function validateFile(file: File, meta: VideoMetadata): string | null {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Only MP4 and MOV files are supported";
  }
  if (file.size > MAX_SIZE) {
    return `File too large — max 500MB (yours is ${(file.size / 1024 / 1024).toFixed(0)}MB)`;
  }
  if (meta.duration > MAX_DURATION) {
    const mm = Math.floor(meta.duration / 60);
    const ss = Math.floor(meta.duration % 60)
      .toString()
      .padStart(2, "0");
    return `Video too long — max 5 minutes (yours is ${mm}:${ss})`;
  }
  if (Math.max(meta.width, meta.height) > MAX_DIMENSION) {
    return "Resolution too high — max 1080p";
  }
  if (!meta.hasAudioTrack) {
    return "This video has no audio track. Please upload a video with audio for transcription.";
  }
  return null;
}
